// @flow
import React, { Component, Fragment } from "react";
import { createPortal } from "react-dom";
import { connect } from "react-redux";
import { type Dispatch } from "redux";
import type { RouterHistory } from "react-router-dom";
import { get } from "lodash";
import mediator from "@tvg/mediator";
import TutorialPanel from "@fdr/atomic-ui/_templates/TutorialPanel";
import ModalV2 from "@fdr/atomic-ui/_templates/ModalV2";
import { setNewPreferenceValue } from "@tvg/shared-actions/UserActions";
import type { OnboardingRoute, Step, Tutorial } from "@tvg/types/OnBoarding";
import type { Device } from "@tvg/types/Device";
import { getAccountNumber } from "@urp/store-selectors";
import {
  type Actions,
  closePrompt,
  closeTutorial,
  openPrompt,
  openTutorial,
  setOnboardingTutorials
} from "@tvg/shared-actions/OnBoardingActions";

import { getOnboardingRoutes, setOnboardingAsSeen } from "./service";

type Props = {
  dispatch: Dispatch<Actions>,
  accountNumber: string,
  device: Device,
  isLoggedIn: boolean,
  tutorials: OnboardingRoute[],
  onboardingsSeen: string,
  isTutorialShown: boolean,
  isPromptShown: boolean,
  gotPreferences: boolean,
  activeTutorial: Tutorial | null,
  history: ?RouterHistory,
  location: {
    hash: string,
    pathname: string,
    search: string
  },
  redirect: boolean | null,
  isEducationalHub?: boolean
};

type State = {
  currentSlide: number
};

/**
 * This component is responsible for fetching the available onboarding tutorials from capi, store them on redux store
 * and managing location/url changes to check if there is any onboarding tutorial available for a given route.
 * When there is an onboarding available, present a prompt/short version if on mobile, on tablet show the tutorial itself directly
 */
export class OnBoardings extends Component<Props, State> {
  static defaultProps = {
    isLoggedIn: false,
    accountNumber: "",
    tutorials: [],
    device: "mobile",
    activeTutorial: null,
    isPromptShown: false,
    isTutorialShown: false,
    onboardingsSeen: "",
    gotPreferences: false,
    location: {
      hash: "",
      search: "",
      pathname: ""
    },
    history: null
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      currentSlide: 0
    };
  }

  componentDidMount() {
    this.getOnboardings().then(
      () => this.props.gotPreferences && this.checkTutorialForRoute()
    );
  }

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.location.pathname !== this.props.location.pathname &&
      this.props.isLoggedIn &&
      this.props.gotPreferences
    ) {
      this.checkTutorialForRoute();
    }

    if (!prevProps.gotPreferences && this.props.gotPreferences) {
      this.checkTutorialForRoute();
    }
  }

  onBackPage = (currentPage: number) => {
    this.setState({ currentSlide: currentPage - 1 });
  };

  onForwardPage = (
    currentPage: number,
    totalSlides: number,
    activeTutorial: Tutorial
  ) => {
    const label =
      currentPage === totalSlides
        ? get(activeTutorial, "finalStep.button.label")
        : get(activeTutorial, "buttonLabel");

    mediator.base.dispatch({
      type: "FDR_TUTORIALS:NEXT",
      payload: {
        buttonText: label,
        linkText: label,
        tutorialCategory: get(activeTutorial, "title"),
        pageNumber: currentPage,
        totalPages: totalSlides
      }
    });

    // Tutorial Viewed event is sent for each unique screen as it renders
    mediator.base.dispatch({
      type: "FDR_TUTORIALS:LOAD_TUTORIAL",
      payload: {
        title: get(activeTutorial, "title"),
        tutorialCategory: get(activeTutorial, "title"),
        pageNumber: currentPage,
        totalPages: totalSlides
      }
    });

    this.setState({ currentSlide: currentPage - 1 });
  };

  getOnboardingsSeen = () =>
    this.props.onboardingsSeen.split(",").filter(Boolean);

  getOnboardings = () =>
    getOnboardingRoutes()
      .then((tutorials) =>
        this.props.dispatch(setOnboardingTutorials(tutorials))
      )
      .catch((err) => console.error(err));

  checkTutorialForRoute = () => {
    const { tutorials, location, device, dispatch, accountNumber } = this.props;

    const tutorialForRoute = tutorials.find((t) =>
      location.pathname.includes(t.route)
    );
    if (tutorialForRoute) {
      const onboardingsSeen = this.getOnboardingsSeen();
      const tutorialId = tutorialForRoute.tutorial.id.toString();

      if (onboardingsSeen.indexOf(tutorialId) < 0) {
        if (device === "tablet") {
          dispatch(openTutorial(tutorialForRoute.tutorial, false));
        } else {
          dispatch(openPrompt(tutorialForRoute.tutorial));
        }

        // $FlowFixMe
        document.body.classList.toggle("body-lock", true);

        setOnboardingAsSeen(+accountNumber, [
          ...onboardingsSeen,
          tutorialId
        ]).then((r) => dispatch(setNewPreferenceValue("onboardingsSeen", r)));
      }
    }
  };

  triggerTutorialFromPrompt = (activeTutorial: Tutorial) => {
    this.props.dispatch(closePrompt());
    this.props.dispatch(openTutorial(activeTutorial, false));

    // $FlowFixMe
    document.body.classList.toggle("body-lock", false);

    mediator.base.dispatch({
      type: "ONBOARDINGS:FIND_OUT_MORE",
      payload: {
        tutorialName: activeTutorial.name
      }
    });
  };

  closeTutorial = (isComplete: boolean = false) => {
    const { activeTutorial, dispatch } = this.props;
    const tutorialName = activeTutorial && activeTutorial.name;
    const totalSlides = activeTutorial ? activeTutorial.steps.length + 1 : 0; // final step

    dispatch(closeTutorial());

    // $FlowFixMe
    document.body.classList.toggle("body-lock", false);

    // means user completed tutorial through final button
    if (isComplete) {
      mediator.base.dispatch({
        type: "ONBOARDINGS:COMPLETE",
        payload: {
          tutorialName,
          destination: activeTutorial && activeTutorial.finalStep.button.route,
          label: activeTutorial && activeTutorial.finalStep.button.label
        }
      });
    } else {
      // or he may have closed the tutorial before completing (closing modal, or closing prompt)
      mediator.base.dispatch({
        type: "FDR_TUTORIALS:CLOSE",
        payload: {
          title: tutorialName,
          tutorialCategory: tutorialName,
          pageNumber: this.state.currentSlide + 1,
          totalPages: totalSlides
        }
      });
    }
    this.setState({ currentSlide: 0 });
  };

  buildTutorials = () => {
    const { activeTutorial, device } = this.props;
    const imageKey = device === "mobile" ? "phoneImage" : "tabletImage";

    const getImage = (step: Step | Tutorial) =>
      step[imageKey] && step[imageKey].length > 1
        ? `/${step[imageKey]}`
        : `/${step.phoneImage}`;

    let tutorials = [];

    if (activeTutorial) {
      if (this.props.isPromptShown) {
        tutorials.push({
          title: activeTutorial.title,
          description: activeTutorial.description,
          image: getImage(activeTutorial)
        });
      } else {
        tutorials = activeTutorial.steps.map((step) => ({
          title: step.title,
          description: step.description,
          image: getImage(step)
        }));

        if (activeTutorial.finalStep && activeTutorial.finalStep.step) {
          tutorials.push({
            title: activeTutorial.finalStep.step.title,
            description: activeTutorial.finalStep.step.description,
            image: getImage(activeTutorial.finalStep.step)
          });
        }
      }
    }

    return tutorials;
  };

  render() {
    const { activeTutorial, isPromptShown, isTutorialShown, device } =
      this.props;

    const element = typeof window !== "undefined" && document.body;

    const tutorials = this.buildTutorials();
    const TutorialTemplate = activeTutorial ? (
      <TutorialPanel
        tutorialTitle={activeTutorial.title}
        isPrompt={isPromptShown}
        isTutorialList={false}
        promptLabel="Find out more"
        promptClick={() =>
          activeTutorial && this.triggerTutorialFromPrompt(activeTutorial)
        }
        buttonLabel={get(
          activeTutorial,
          "finalStep.button.label",
          "Great! Let's bet"
        )}
        buttonNav={get(activeTutorial, "finalStep.button.route", "")}
        highlightedText={isPromptShown ? "New Feature" : ""}
        tutorials={tutorials}
        isNew={get(activeTutorial, "isNew", false)}
        finalStepButtonClick={() => this.closeTutorial(true)}
        onClose={() => this.closeTutorial(false)}
        device={device}
        onForwardPage={(currentPage, totalSlides) =>
          this.onForwardPage(currentPage, totalSlides, activeTutorial)
        }
        onBackPage={this.onBackPage}
      />
    ) : null;

    const modalBaseProps = {
      title: activeTutorial ? activeTutorial.title : "",
      titleType: "default",
      isOpen: isTutorialShown,
      onClose: this.closeTutorial,
      isFullHeight: true,
      qaLabel: "onboardingModal",
      hasShadow: false,
      hideFixedBanner: this.props.isEducationalHub
    };

    const modalProps =
      device === "mobile"
        ? { ...modalBaseProps, animation: "bottom" }
        : {
            ...modalBaseProps,
            animation: "fade",
            hasOverlay: true,
            isFullWidth: false,
            isFullHeight: false,
            isFluidWidth: true,
            offsetTop: 0
          };

    const TutorialModal = (
      <ModalV2 {...modalProps}>{() => TutorialTemplate}</ModalV2>
    );

    return this.props.device === "tablet"
      ? TutorialModal
      : element &&
          createPortal(
            <Fragment>
              {isPromptShown && TutorialTemplate}
              {TutorialModal}
            </Fragment>,
            element
          );
  }
}

export default connect(
  (store) => ({
    accountNumber: getAccountNumber(store),
    isLoggedIn: get(store, "userData.logged", false),
    onboardingsSeen: get(store, "userData.preferences.onboardingsSeen", ""),
    gotPreferences: get(store, "userData.gotPreferences", false),
    tutorials: store.onboardings.tutorials,
    isPromptShown: store.onboardings.isPromptShown,
    isTutorialShown: store.onboardings.isTutorialShown,
    activeTutorial: store.onboardings.activeTutorial,
    redirect: store.onboardings.redirect
  }),
  (dispatch: Dispatch<Actions>) => ({ dispatch })
)(OnBoardings);
